import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { HitAvatar, nextFx } from "@/components/avatar/HitAvatar";
import { RankName, frameOf } from "@/components/avatar/RankPanel";
import { cardImageUrl } from "@/lib/api";
import { useCountdown } from "@/components/AuctionCard";
import { Label, ClockPill, Badge, money, stageBg, GLOW } from "@/components/auction/AuctionUI";
import { ImageOff, Maximize2, X, ShieldAlert, Lock, Trophy, AlertTriangle, BadgeCheck, Star } from "lucide-react";
import { HitButton } from "@/components/loading";
import { OrderRateButton } from "@/components/OrderRateButton";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function Img({ path, size, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (!path || failed) return <div className={`flex items-center justify-center text-neutral-600 ${className}`}><ImageOff size={22} /></div>;
  return <img src={cardImageUrl(path, size)} alt={alt} loading="lazy" onError={() => setFailed(true)} className={`object-cover ${className}`} />;
}

export function Gallery({ photos, alt, glow }) {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(false);
  const cur = photos[i];
  const thumb = (p, idx, cls) => (
    <button key={p + idx} type="button" onClick={(e) => { e.stopPropagation(); setI(idx); }} data-testid={`auction-gallery-thumb-${idx}`}
            className={`${cls} flex-none rounded-[10px] overflow-hidden bg-[#1b1b1b] border-2 transition-colors ${idx === i ? "border-[#FF4438]" : "border-white/15"}`}>
      <Img path={p} size={200} alt="" className="w-full h-full" />
    </button>
  );
  return (
    <div className="flex gap-3" data-testid="auction-gallery">
      {photos.length > 1 && <div className="hidden lg:flex flex-col gap-3">{photos.map((p, idx) => thumb(p, idx, "w-[76px] h-[104px]"))}</div>}
      <div className="relative flex-1 h-[min(62vh,440px)] lg:h-auto lg:aspect-[63/44] rounded-[22px] overflow-hidden border border-white/10 flex items-center justify-center px-3 pt-3 pb-14 lg:p-6" style={stageBg(glow)}>
        <button type="button" onClick={() => cur && setZoom(true)} data-testid="auction-gallery-open" className="w-full h-full flex items-center justify-center">
          {cur ? <img src={cardImageUrl(cur, 1200)} alt={alt} className="max-w-full max-h-full w-auto h-auto object-contain rounded-[10px]"
                      style={{ boxShadow: `0 20px 40px rgba(0,0,0,.6), 0 0 40px ${GLOW[glow]}` }} />
               : <Img path={null} alt={alt} className="w-full h-full" />}
        </button>
        {photos.length > 1 && <div className="lg:hidden absolute left-3 bottom-3 flex gap-2">{photos.slice(0, 4).map((p, idx) => thumb(p, idx, "w-10 h-9"))}</div>}
        {cur && (
          <button type="button" onClick={() => setZoom(true)} data-testid="auction-gallery-zoom-btn"
                  className="sx-mono absolute right-3 bottom-3 text-[10px] tracking-[.1em] uppercase bg-black/70 border border-white/15 rounded-full px-3 py-2 flex items-center gap-1.5">
            <Maximize2 size={11} /> Ampliar · {i + 1}/{photos.length}
          </button>
        )}
      </div>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={() => setZoom(false)} data-testid="auction-gallery-zoom">
          <button type="button" className="absolute top-3 right-3 text-white p-2" aria-label="Cerrar" data-testid="auction-gallery-zoom-close"><X size={24} /></button>
          <img src={cardImageUrl(cur, 1600)} alt={alt} className="flex-1 min-h-0 w-full object-contain p-4" onClick={(e) => e.stopPropagation()} data-testid="auction-gallery-main" />
          {photos.length > 1 && <div className="flex gap-2 justify-center p-3">{photos.map((p, idx) => thumb(p, idx, "w-12 h-16"))}</div>}
        </div>
      )}
    </div>
  );
}

export function SplitClock({ endsAt, tone = "coral", testid = "detail-countdown" }) {
  const c = useCountdown(endsAt) || "";
  const [d, hms] = c.includes("d ") ? c.split(" ") : [null, c];
  return (
    <div className="flex gap-1.5 justify-end" data-testid={testid}>
      {d && <ClockPill tone={tone} className="text-[17px]">{d}</ClockPill>}
      <ClockPill tone={tone} className="text-[17px]">{hms}</ClockPill>
    </div>
  );
}

export function PriceHead({ a, big = "text-[40px] lg:text-[52px]" }) {
  const has = a.current_bid != null;
  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <Label>{has ? "Puja actual" : "Aún sin pujas"}</Label>
        <div className={`sx-price mt-1.5 ${big}`} style={{ color: has ? "#34D399" : "#fff" }} data-testid="detail-current-bid">{money(has ? a.current_bid : a.starting_price)}</div>
        <div className="text-[13px] text-white/50 mt-1.5">Salida {money(a.starting_price)} · {plural(a.bid_count || 0, "puja")}</div>
      </div>
      <div className="flex-none text-right">
        <Label className="mb-1.5">Termina en</Label>
        <SplitClock endsAt={a.ends_at} />
      </div>
    </div>
  );
}

export function BidInput({ value, onChange, disabled, onFocus, onBlur }) {
  return (
    <div className="relative flex-1">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45 font-bold">$</span>
      <input type="number" inputMode="numeric" step="1" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
             onFocus={onFocus} onBlur={onBlur}
             data-testid="bid-amount-input" className="sx-input w-full h-[54px] pl-9 pr-3 text-xl font-extrabold disabled:opacity-50" />
    </div>
  );
}

export function BidForm({ a, amount, setAmount, onSubmit, busy, done, cta = "Pujar", chips = true }) {
  const m = a.min_next_bid;
  const { user } = useAuth();
  const [focus, setFocus] = useState(false);
  const [fx, setFx] = useState("");
  useEffect(() => { if (done) setFx(nextFx("bid")); }, [done]);
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3" data-testid="bid-form">
      <div className="text-[13px] text-white/55">Puja mínima: <b className="text-white" data-testid="bid-min">${m}</b> · solo números enteros</div>
      <div className="flex gap-2 items-center">
        {user && <HitAvatar avatarId={user.avatar_id} name={user.name} size={44} fx={fx} rank={frameOf(user)} state={focus && String(amount || "").length > 0 ? "thinking" : "active"} testid="bid-form-avatar" />}
        <BidInput value={amount} onChange={setAmount} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} />
        <HitButton type="submit" loading={busy} done={done} loadingText="Enviando…" doneText="Puja enviada" tone="dark" data-testid="bid-submit-btn"
                   className={`sx-btn h-[54px] min-w-[136px] ${busy || done ? "px-4" : "px-6"} rounded-[14px] text-base whitespace-nowrap inline-flex items-center justify-center gap-2`}>{cta}</HitButton>
      </div>
      {chips && (
        <div className="grid grid-cols-3 gap-2">
          {[m, m + 2, m + 4].map((v, idx) => (
            <button key={v} type="button" onClick={() => setAmount(String(v))} data-testid={`bid-chip-${idx}`}
                    className={`sx-mono h-9 rounded-full text-xs border transition-colors ${String(v) === String(amount) ? "border-[#FF4438]/70 bg-[#FF4438]/10" : "border-white/14 bg-white/[.03] hover:border-white/30"}`}>${v}</button>
          ))}
        </div>
      )}
    </form>
  );
}

export function PayWarning({ days }) {
  return (
    <div className="rounded-[18px] border border-[#FF4438]/55 bg-[#FF4438]/[.08] p-4 flex gap-3 text-[13px] leading-relaxed text-white/70" data-testid="bid-pay-warning">
      <ShieldAlert size={16} className="text-[#FF4438] flex-none mt-0.5" />
      <p><b className="text-white">Pujar es un compromiso de pago.</b> Pujar no cobra nada ahora. Si <b className="text-white">GANAS</b>, pagas dentro de <b className="text-white">{plural(days, "día")}</b> desde el cierre. Si no registras el pago a tiempo, <b className="text-[#ff6b61]">tu cuenta será BANEADA</b>.</p>
    </div>
  );
}

export function LeadingPanel({ a }) {
  return (
    <div className="rounded-[22px] border border-[#34D399]/45 bg-[#101010] overflow-hidden" data-testid="detail-you-leading">
      <div className="flex items-center justify-between px-4 py-3 bg-[#34D399]/[.07] border-b border-[#34D399]/20">
        <span className="sx-mono text-[11px] tracking-[.14em] text-[#34D399] flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#34D399] shadow-[0_0_0_4px_rgba(52,211,153,.2)]" /> VAS GANANDO</span>
        <span className="text-xs text-white/60">Tu puja es la más alta</span>
      </div>
      <div className="p-4 flex items-end justify-between gap-3">
        <div><Label>Tu puja</Label><div className="sx-price text-[34px] text-[#34D399] mt-1">{money(a.current_bid)}</div></div>
        <SplitClock endsAt={a.ends_at} />
      </div>
    </div>
  );
}

export function OutbidPanel({ a, myBest, amount, setAmount, onSubmit, busy, done }) {
  const c = useCountdown(a.ends_at);
  return (
    <div className="rounded-[22px] border border-[#FF4438]/70 bg-[#101010] overflow-hidden shadow-[0_0_30px_rgba(255,68,56,.18)]" data-testid="detail-outbid">
      <div className="p-4 bg-gradient-to-b from-[#FF4438]/[.16] to-transparent">
        <div className="sx-mono text-[11px] tracking-[.14em] text-[#ff6b61] flex items-center gap-2"><AlertTriangle size={13} /> TE SUPERARON</div>
        <div className="sx-h text-[26px] mt-2">Alguien pujó <span className="text-[#34D399]">{money(a.current_bid)}</span></div>
        <p className="text-[13px] text-white/70 mt-1.5">Tu puja de {money(myBest)} ya no es la más alta. Aún quedan <span className="sx-mono text-[#FF4438]">{c}</span>.</p>
      </div>
      <div className="p-4 border-t border-white/10">
        <BidForm a={a} amount={amount} setAmount={setAmount} onSubmit={onSubmit} busy={busy} done={done} cta="Volver a pujar" />
      </div>
    </div>
  );
}

export function BlockedPanel({ a, leading }) {
  const r = a.bid_block_reason;
  const [strong, ...rest] = r.split(". ");
  return (
    <div className="sx-card p-4 space-y-3">
      <div className="flex gap-2 opacity-60">
        <div className="relative flex-1"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45 font-bold">$</span>
          <div className="sx-input h-[54px] pl-9 flex items-center text-xl font-extrabold text-white/45">{a.min_next_bid}</div></div>
        <div className="h-[54px] px-6 rounded-[14px] bg-white/10 text-white/50 font-extrabold flex items-center">Pujar</div>
      </div>
      <div data-testid="bid-blocked" className={`rounded-[14px] p-3 flex items-start gap-2.5 text-[13px] leading-relaxed ${leading ? "border border-[#34D399]/40 bg-[#34D399]/[.06] text-white/70" : "border border-white/14 bg-white/[.03] text-white/70"}`}>
        <Lock size={14} className={`flex-none mt-0.5 ${leading ? "text-[#34D399]" : "text-white/60"}`} />
        <span>
          {rest.length ? <><b className={leading ? "text-[#34D399]" : "text-white"}>{strong}.</b> {rest.join(". ")}</> : r}
          {r.startsWith("Inicia") && <Link to="/login" className="text-[#FF4438] underline ml-1" data-testid="bid-login-link">Ingresar</Link>}
        </span>
      </div>
    </div>
  );
}

export const RESULT_TEXT = {
  no_bids: "Terminó sin pujas.",
  awaiting_payment: "Cerrada. Esperando el pago del ganador.",
  repechage_offered: "Cerrada. En repechaje: se ofreció al siguiente postor.",
  processing_default: "Cerrada. Procesando repechaje.",
  sold: "Vendida.",
  unsold: "Cerrada sin venta (ningún postor completó el pago).",
  finalized: "Finalizada.",
};

export function EndedPanel({ a }) {
  const showPrice = a.final_price != null;
  const extra = !a.public_result_view && a.winner_name && a.result === "sold" ? ` Ganador: ${a.winner_name} · $${a.final_price}` : "";
  return (
    <div className="sx-card p-4 space-y-3" data-testid="auction-ended-panel">
      <div className="flex items-center gap-2"><Badge tone={a.result === "sold" ? "green" : "gray"}>{a.result === "sold" ? "✓ Vendida" : "Finalizada"}</Badge></div>
      <div className="flex items-end justify-between gap-3">
        <div>
          <Label>{showPrice ? "Precio final" : "Estado"}</Label>
          <div className="sx-price text-[40px] mt-1.5" style={{ color: showPrice ? "#34D399" : "rgba(255,255,255,.6)" }} data-testid="detail-current-bid">{showPrice ? money(a.final_price) : "Finalizada"}</div>
          <div className="text-[13px] text-white/50 mt-1.5">Salida {money(a.starting_price)} · {plural(a.bid_count || 0, "puja")}</div>
        </div>
        <ClockPill tone="gray" testid="detail-countdown">{a.result === "sold" ? "PAGADA" : "CERRADA"}</ClockPill>
      </div>
      <p className="text-sm text-white/70" data-testid="auction-result-text">{(RESULT_TEXT[a.result] || "Finalizada.") + extra}</p>
      {(a.my_offer?.order_id || a.offer_status?.order_id) && <OrderRateButton orderId={a.my_offer?.order_id || a.offer_status?.order_id} />}
      <Link to="/subastas" data-testid="auction-ended-browse" className="sx-btn-ghost h-[52px] flex items-center justify-center text-[15px]">Ver subastas activas →</Link>
    </div>
  );
}

export function BidHistory({ bids }) {
  const topKey = bids[0] ? `${bids[0].created_at}-${bids[0].amount}` : "";
  const prevTop = useRef(topKey);
  const [fresh, setFresh] = useState(null);
  useEffect(() => {
    if (!topKey || topKey === prevTop.current) return;
    const hadPrev = !!prevTop.current;
    prevTop.current = topKey;
    if (!hadPrev) return;
    const diff = bids[1] ? bids[0].amount - bids[1].amount : null;
    setFresh({ key: topKey, fx: nextFx("bid"), amount: diff != null ? `+$${Number(diff).toFixed(2)}` : `$${Number(bids[0].amount).toFixed(2)}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey]);
  return (
    <div className="sx-card p-4" data-testid="bid-history">
      <div className="flex items-center justify-between mb-3">
        <span className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438]">HISTORIAL DE PUJAS</span>
        <span className="sx-label">{plural(bids.length, "puja")}</span>
      </div>
      {bids.length === 0 ? <p className="text-sm text-white/50">Aún no hay pujas. ¡Sé el primero!</p> : (
        <ul className="space-y-1">
          {bids.map((b, idx) => {
            const isFresh = idx === 0 && fresh?.key === topKey;
            return (
            <li key={b.created_at + idx} data-testid={`bid-row-${idx}`}
                className={`flex items-center justify-between gap-3 px-3.5 py-3 rounded-[14px] ${idx === 0 ? "bg-[#34D399]/[.08] border border-[#34D399]/35" : ""} ${isFresh ? "hit-bid-in" : ""}`}>
              <span className="flex items-center gap-2.5 min-w-0">
                {idx === 0 ? <Trophy size={16} className="text-amber-400 flex-none" /> : <span className="w-4 flex-none" />}
                <HitAvatar avatarId={b.avatar_id} name={b.bidder_name} size={36} lite rank={b.rank || "common"} fx={isFresh ? fresh.fx : undefined} amount={isFresh ? fresh.amount : undefined} testid={`bid-avatar-${idx}`} />
                <span className="min-w-0">
                  <span className={`block truncate ${idx === 0 ? "font-bold text-white" : "text-white/75"}`}>{b.bidder_name}{b.is_you && <span className="text-[#FF4438]"> (tú)</span>}</span>
                  <span className="sx-mono block text-[10px] text-white/40 font-medium">{new Date(b.created_at).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "medium" })}</span>
                </span>
              </span>
              <b className={`sx-price ${idx === 0 ? "text-[#34D399] text-lg" : "text-white/70 text-base"}`}>{money(b.amount)}</b>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Row({ label, children, mono }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-[13px]">
      <span className="text-white/55">{label}</span>
      <span className={`text-white text-right ${mono ? "sx-mono text-xs" : ""}`}>{children}</span>
    </div>
  );
}

export function InfoCard({ a }) {
  const s = a.seller || {};
  return (
    <div className="sx-card p-4" data-testid="auction-info-card">
      {s.name && (
        <div className="flex items-center gap-3 pb-4 mb-3 border-b border-white/10">
          <span className="flex flex-col items-center gap-1 shrink-0"><HitAvatar avatarId={s.avatar_id} name={s.name} size={56} lite rank={frameOf(s)} testid="auction-seller-avatar" /><RankName rank={s.rank || "common"} className="!text-[8px]" /></span>
          <div className="min-w-0">
            <div className="font-bold flex items-center gap-1.5">{s.name}{s.verified && <BadgeCheck size={15} className="text-[#34D399]" />}
              {s.rating_avg ? <span className="text-amber-400 text-xs inline-flex items-center gap-0.5 ml-1"><Star size={11} fill="currentColor" />{s.rating_avg} ({s.rating_count})</span> : null}</div>
            <div className="text-xs text-white/55">{s.verified ? "Vendedor verificado · " : ""}{a.city}, {a.province}</div>
          </div>
        </div>
      )}
      {(a.set_name || a.rarity_text) && <Row label="Set / rareza">{[a.set_name, a.rarity_text].filter(Boolean).join(" · ")}</Row>}
      <Row label="Duración">{plural(a.duration_days, "día")}</Row>
      {a.payment_deadline_days && <Row label="Plazo de pago del ganador">{plural(a.payment_deadline_days, "día")}</Row>}
      <Row label="Envío">{money(a.shipping_cost_same_province)} en {a.province} · {money(a.shipping_cost_other_province)} otras</Row>
      {a.accepts_local_delivery && <Row label="Entrega personal">{a.meetup_location}</Row>}
      {a.ends_at && <Row label="Cierre" mono>{new Date(a.ends_at).toLocaleString("es-EC", { dateStyle: "medium", timeStyle: "short" })}</Row>}
      {a.description && <p className="text-[13px] text-white/70 mt-3 pt-3 border-t border-white/10 whitespace-pre-line leading-relaxed" data-testid="auction-detail-description">{a.description}</p>}
    </div>
  );
}
